import type { PromptVariableListItem } from './prompt.types'

/**
 * A list's options as editable text, one per line.
 *
 * `value: Label | prompt text` — the label defaults to the value, and the prompt
 * text is omitted whenever it would just repeat the value.
 *
 * Text rather than a row editor because a list is usually pasted in from wherever
 * it already exists, and a per-row form turns one paste into fifty clicks. The
 * parsed result is echoed back before saving so the shorthand is never guesswork.
 *
 * Shared by the create and edit dialogs: two copies of a format is how one of them
 * quietly stops round-tripping.
 */

/** `value: Label | prompt text` → an item. Only `value` is required. */
export function parseListItems(text: string): PromptVariableListItem[] {
	return text
		.split('\n')
		.map((line) => line.trim())
		.filter(Boolean)
		.map((line) => {
			// The pipe splits first: a label may contain a colon ("Indoor · Vet: exam"),
			// and the render text may contain both.
			const [head, ...tail] = line.split('|')
			const renderAs = tail.join('|').trim()

			const [value, ...labelParts] = (head ?? '').split(':')
			const code = (value ?? '').trim()
			const label = labelParts.join(':').trim() || code

			return { value: code, label, ...(renderAs === '' ? {} : { renderAs }) }
		})
		.filter((item) => item.value !== '')
}

/** The inverse, so an existing list opens as the text that would recreate it. */
export function formatListItems(items: PromptVariableListItem[]): string {
	return items
		.map((item) => {
			const head = item.label === item.value ? item.value : `${item.value}: ${item.label}`
			// Omitted when it adds nothing — a round-tripped list should not grow a
			// column of text identical to its own values.
			return item.renderAs === undefined || item.renderAs === item.value ? head : `${head} | ${item.renderAs}`
		})
		.join('\n')
}
